import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Layers, Settings as SettingsIcon, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/super", label: "Overview", icon: LayoutDashboard, end: true, testid: "super-nav-dashboard" },
  { to: "/super/companies", label: "Companies", icon: Building2, testid: "super-nav-companies" },
  { to: "/super/company-types", label: "Company Types", icon: Layers, testid: "super-nav-company-types" },
  { to: "/super/login-attempts", label: "Login Attempts", icon: Layers, testid: "super-nav-login-attempts" },
  { to: "/super/plans", label: "Plans", icon: Layers, testid: "super-nav-plans" },
  { to: "/super/subscriptions", label: "Subscriptions", icon: Building2, testid: "super-nav-subscriptions" },
  { to: "/super/subscription-invoices", label: "Subscription Invoices", icon: Layers, testid: "super-nav-sub-invoices" },
  { to: "/super/settings", label: "Platform Settings", icon: SettingsIcon, testid: "super-nav-settings" },
];

export default function SuperLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Force dark mode for super admin
  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => document.documentElement.classList.remove("dark");
  }, []);

  return (
    <div className="min-h-screen flex bg-[#09090B] text-zinc-200">
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static inset-y-0 left-0 z-40 w-64 bg-[#0a0a0c] border-r border-white/5 flex flex-col transition-transform`}
        data-testid="super-sidebar"
      >
        <div className="px-6 h-16 flex items-center justify-between border-b border-white/5">
          <Link to="/super" className="font-serif text-2xl tracking-tight text-white">
            EventFlow <span className="text-xs uppercase tracking-[0.25em] font-sans text-zinc-500 ml-1">admin</span>
          </Link>
          <button className="md:hidden" onClick={() => setOpen(false)} aria-label="Close sidebar">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                  isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`
              }
              onClick={() => setOpen(false)}
            >
              <n.icon size={18} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="px-2 py-3 text-xs">
            <div className="text-zinc-400 truncate">{user?.name}</div>
            <div className="text-zinc-500 truncate">{user?.email}</div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-zinc-300 hover:bg-white/5 hover:text-white"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="super-logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-[#09090B]/80 backdrop-blur-xl border-b border-white/5 flex items-center px-4 md:px-8 justify-between">
          <button className="md:hidden p-2" onClick={() => setOpen(true)} aria-label="Open sidebar">
            <Menu size={20} />
          </button>
          <div className="ml-auto flex items-center gap-3">
            <div className="md:hidden text-xs bg-[#FEF6E4] border border-[#F2D9A6] text-[#7C4A03] px-3 py-1 rounded">Please use mobile.planniq.in for better experience in mobile</div>
            <span className="text-xs uppercase tracking-[0.25em] text-zinc-500">Super Admin</span>
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white text-black text-sm font-medium">
              {(user?.name || "S").slice(0, 1).toUpperCase()}
            </span>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
